import React, { type FC } from 'react';

import { useField } from 'formik';

import { Box } from '@clutter/clean';

import { Button, Table } from '@shared/components/bootstrap';
import { useToggle } from '@shared/hooks/use_toggle';

import {
  Dialog as ConfirmationDialog,
  Response as ConfirmationResponse,
  useConfirmation,
} from '@admin/components/confirmation';
import { Panel } from '@admin/components/helpers/panel';
import type { Landing__Location__ExplanationInput } from '@admin/schema';

import { LandingLocationExplanationModal } from './modal';

const LandingLocationExplanationDeleteButton: FC<{
  explanation: Landing__Location__ExplanationInput;
  onDelete(): void;
}> = ({ explanation, onDelete }) => {
  const { confirmation, confirm } = useConfirmation();
  const onClick = async () => {
    const confirmed = await confirm({
      title: <>Are you sure you want to delete {explanation.title}?</>,
      description: <>Deleting is permanent.</>,
    });
    if (confirmed !== ConfirmationResponse.Confirm) return;
    onDelete();
  };

  return (
    <>
      {confirmation && <ConfirmationDialog confirmation={confirmation} />}
      <Button kind="danger" onClick={onClick}>
        Delete
      </Button>
    </>
  );
};

const LandingLocationExplanationEditButton: FC<{
  Explanation: Landing__Location__ExplanationInput;
  onSave(_: Landing__Location__ExplanationInput): void;
}> = ({ Explanation, onSave }) => {
  const { visible, onToggle } = useToggle();
  return (
    <>
      {visible && (
        <LandingLocationExplanationModal
          onSave={(input) => {
            onSave(input);
            onToggle();
          }}
          onClose={() => onToggle()}
          value={Explanation}
        />
      )}
      <Button kind="primary" onClick={() => onToggle()}>
        Edit
      </Button>
    </>
  );
};

const LandingLocationExplanationNewButton: FC<{
  onSave(_: Landing__Location__ExplanationInput): void;
}> = ({ onSave }) => {
  const { visible, onToggle } = useToggle();
  return (
    <>
      <Button kind="primary" onClick={() => onToggle()}>
        Add
      </Button>
      {visible && (
        <LandingLocationExplanationModal
          onSave={(input) => {
            onSave(input);
            onToggle();
          }}
          onClose={() => onToggle()}
        />
      )}
    </>
  );
};

export const LandingLocationExplanationsPanel: FC = () => {
  const [field, _, helpers] = useField<Array<Landing__Location__ExplanationInput>>('explanations');

  return (
    <Panel>
      <Panel.Header>
        <Panel.Title>Explanations</Panel.Title>
      </Panel.Header>
      <Panel.Body>
        <Table striped>
          <thead>
            <tr>
              <th className="text-left">Title</th>
              <th className="text-left">Description</th>
              <th className="col-zero" />
            </tr>
          </thead>
          <tbody>
            {field.value.map(
              (explanation, index) =>
                !explanation._destroy && (
                  <tr key={index}>
                    <td className="text-left">{explanation.title}</td>
                    <td className="text-left">{explanation.description}</td>
                    <td className="col-zero">
                      <Box.Flex gap="4px">
                        <LandingLocationExplanationEditButton
                          Explanation={explanation}
                          onSave={(input) => {
                            const explanations = [...field.value];
                            explanations[index] = {
                              ...explanations[index],
                              ...input,
                            };
                            helpers.setValue(explanations);
                          }}
                        />
                        <LandingLocationExplanationDeleteButton
                          explanation={explanation}
                          onDelete={() => {
                            const explanations = [...field.value];
                            explanations[index] = {
                              ...explanations[index],
                              _destroy: !explanations[index]._destroy,
                            };
                            helpers.setValue(explanations);
                          }}
                        />
                      </Box.Flex>
                    </td>
                  </tr>
                ),
            )}
          </tbody>
        </Table>
      </Panel.Body>
      <Panel.Footer align="right">
        <LandingLocationExplanationNewButton
          onSave={(input) => {
            const explanations = [...field.value];
            explanations.push(input);
            helpers.setValue(explanations);
          }}
        />
      </Panel.Footer>
    </Panel>
  );
};
